
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import PackageCard from './components/PackageCard';
import PackageComparison from './components/PackageComparison';
import InvestmentCalculator from './components/InvestmentCalculator';

export default function InvestmentPackages() {
  useEffect(() => {
    // Add Schema.org JSON-LD for investment packages page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FinancialProduct",
      "name": "Glamping Investment Packages - OED Tiny Homes",
      "description": "Choose from our curated investment packages for glamping retreat developments. Starter, Growth, and Premium packages available with returns of 15-25% ROI.",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/investment-packages`,
      "provider": {
        "@type": "Organization",
        "name": "OED Tiny Homes and Glamping Retreat Developments"
      },
      "offers": [
        {
          "@type": "Offer",
          "name": "Starter Package",
          "description": "Entry-level investment package for new investors",
          "priceSpecification": {
            "@type": "PriceSpecification",
            "minPrice": "25000",
            "priceCurrency": "USD"
          }
        },
        {
          "@type": "Offer", 
          "name": "Growth Package",
          "description": "Balanced investment package for experienced investors",
          "priceSpecification": {
            "@type": "PriceSpecification",
            "minPrice": "75000",
            "priceCurrency": "USD"
          }
        },
        {
          "@type": "Offer",
          "name": "Premium Package", 
          "description": "High-value investment package for serious investors",
          "priceSpecification": {
            "@type": "PriceSpecification",
            "minPrice": "150000",
            "priceCurrency": "USD"
          }
        }
      ]
    });
    document.head.appendChild(script);

    // Update page title and meta
    document.title = "Investment Packages | Glamping Development Opportunities | OED Tiny Homes";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Choose from our curated glamping investment packages. Starter, Growth, and Premium options with 15-25% ROI. Start investing in luxury outdoor hospitality developments today.');
    }

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(s => s.remove());
    };
  }, []);

  const [selectedPackage, setSelectedPackage] = useState('growth');

  const packages = [
    {
      id: 'starter',
      name: 'Starter Package',
      price: 25000,
      maxPrice: 50000,
      targetReturn: '15-18%',
      timeline: '2-3 years',
      description: 'Perfect for first-time glamping investors looking to enter the market.',
      features: [
        'Access to 2-3 development projects',
        'Quarterly progress reports',
        'Basic investor portal access',
        'Annual investor meetings',
        'Email support',
        'Minimum 15% projected returns'
      ],
      investments: [
        'Georgia Peach Orchard Retreat',
        'South Carolina Lowcountry Lodge',
        'Tennessee Valley Escape (Phase 1)'
      ],
      benefits: [
        'Low barrier to entry',
        'Diversified portfolio start',
        'Learn the glamping market',
        'Build investment experience'
      ],
      ideal: 'New investors, conservative risk tolerance, learning-focused'
    },
    {
      id: 'growth',
      name: 'Growth Package',
      price: 75000,
      maxPrice: 125000,
      targetReturn: '18-22%',
      timeline: '3-4 years',
      description: 'Balanced approach for investors seeking steady growth and higher returns.',
      features: [
        'Access to 4-6 development projects',
        'Monthly progress reports',
        'Premium investor portal',
        'Quarterly investor calls',
        'Priority customer support',
        'Site visit opportunities',
        'Minimum 18% projected returns'
      ],
      investments: [
        'Blue Ridge Mountain Haven',
        'Alabama Gulf Coast Sanctuary',
        'Tennessee Valley Escape',
        'Georgia Peach Orchard Retreat',
        'Desert Oasis Expansion'
      ],
      benefits: [
        'Higher return potential',
        'Broader project access',
        'Enhanced reporting',
        'Direct project involvement'
      ],
      ideal: 'Experienced investors, moderate risk tolerance, growth-focused',
      popular: true
    },
    {
      id: 'premium',
      name: 'Premium Package',
      price: 150000,
      maxPrice: 500000,
      targetReturn: '22-25%',
      timeline: '4-5 years',
      description: 'Exclusive access to high-value developments with maximum return potential.',
      features: [
        'Access to ALL development projects',
        'Weekly progress updates',
        'VIP investor portal with analytics',
        'Monthly one-on-one calls',
        'Dedicated account manager',
        'Exclusive site visits and events',
        'Early access to new opportunities',
        'Minimum 22% projected returns',
        'Exit strategy consultation'
      ],
      investments: [
        'Coastal Cliff Sanctuary',
        'Mountain Peak Lodge',
        'Wine Country Retreat',
        'Lakeside Adventure Base',
        'All current and future projects'
      ],
      benefits: [
        'Maximum return potential',
        'Exclusive project access',
        'White-glove service',
        'Strategic partnership opportunities'
      ],
      ideal: 'High-net-worth investors, aggressive growth, partnership-focused'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
            style={{
              backgroundImage: `url('https://public.readdy.ai/ai/img_res/644b8633df5e10863d931c4e8c588643.jpg')`
            }}
          />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                Investment Packages
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                Choose the investment package that matches your goals and experience level. 
                Each package provides access to our carefully curated glamping development opportunities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <div className="bg-white rounded-lg p-6 shadow-lg">
                  <div className="text-3xl font-bold text-emerald-600">3</div>
                  <div className="text-gray-600">Package Options</div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-lg">
                  <div className="text-3xl font-bold text-emerald-600">15-25%</div>
                  <div className="text-gray-600">Target Returns</div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-lg">
                  <div className="text-3xl font-bold text-emerald-600">$25K+</div>
                  <div className="text-gray-600">Starting Investment</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Package Cards */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Choose Your Investment Level</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Each package is designed to meet different investor needs and experience levels. 
                All packages include access to our proven development projects and professional management.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {packages.map((pkg) => (
                <PackageCard 
                  key={pkg.id} 
                  package={pkg} 
                  isSelected={selectedPackage === pkg.id}
                  onSelect={() => setSelectedPackage(pkg.id)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Package Comparison */}
        <PackageComparison packages={packages} />

        {/* Investment Calculator */}
        <InvestmentCalculator packages={packages} />

        {/* How It Works */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">How Investment Packages Work</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Our streamlined process makes it easy to start investing in glamping developments.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">1</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Choose Package</h3>
                <p className="text-gray-600">Select the investment package that matches your goals and budget.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Complete Application</h3>
                <p className="text-gray-600">Submit your investment application and required documentation.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">3</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Fund Investment</h3>
                <p className="text-gray-600">Transfer funds securely through our verified investment platform.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">4</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Track Progress</h3>
                <p className="text-gray-600">Monitor your investments through our investor portal and regular updates.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-emerald-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Start Investing?</h2>
            <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
              Join our network of successful investors and be part of the growing glamping industry.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/start-investing"
                className="bg-white text-emerald-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Apply Now
              </Link>
              <a
                href="tel:256-268-7146"
                className="bg-emerald-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap"
              >
                Schedule Consultation
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
